import dayjs from 'dayjs';
import {formatCommaNumber} from '@/utils/utils';
import {ModuSignMethod} from '@/views/ContractManagement/enums';

/**
 * 물품매매V6(2차 개정 서식) 계약 생성 요청 바디 조립 — 순수 함수.
 *
 * 출처: mulpumV5Payload.js 복제본. V5 와의 차이는 세 가지뿐이다(근거:
 * contracts-api `dentium-product-sale-v6.create.dto.ts` 머리 주석, PHASE1-FIELDS.md):
 *   ① paymentDetails 가 V1(모두싸인 시대) 모양으로 되돌아갔다 — 방식이 방법에 중첩된
 *      `{paymentMethod, paymentFinancialCompany?, paymentOption?{option, value?}, paymentDate}`.
 *      `기타`는 paymentOption 자체를 보내지 않는다(서버 V6 매퍼가 등록 명세에 없는 칸
 *      `기타{방식}1` 을 fail-closed 로 막는다 — 생성 라우트는 본문을 DTO 데코레이터로 검사하지 않는다).
 *   ② 비고(description)가 되살아났다.
 *   ③ confirmInfo.center(영업센터)가 추가됐다.
 *   ④ 단가 할인율을 품목별로 보낸다(itemDiscountRates, 2026-10-21) — 계약당 1칸 discountRate 는 더 이상
 *      보내지 않는다. 서버는 품목별 값을 계약서 칸에 그대로 찍고, 대표값은 금액 가중 평균으로 저장한다.
 *
 * state 인자는 컴포넌트 data() 필드명을 그대로 반영한 얕은 매핑이다. MulpumTypeV6.vue 의
 * createContract()/makeContractDraft()/createTempContract() 가 this.* 를 모아 호출한다.
 *
 * 검증: scripts/contracts-v6-preserve-check.js 가 이 파일이 보내는 키를 서버 DTO 허용 목록과
 * 대조한다.
 */

/**
 * epoch(ms)/dayjs 값을 'YYYY-MM-DD' 문자열로 변환한다. v1 getDateFromEpochTime()과 동일.
 */
export function getDateFromEpochTime(value) {
  if (!value) return '';
  return dayjs(value).format('YYYY-MM-DD');
}

/**
 * MulpumTypeV6(2차 개정 서식) 폼 state → POST /contracts/create-by-template
 * (및 temp/embedded-drafts) 요청 바디.
 */
export function buildMulpumTypeV6Payload(state) {
  const {
    templateId,
    contractType,
    contractTitle,
    contractCreator,
    clientData,
    itemAmounts,
    promotionCode,
    itemDiscountRates,
    selectedPaymentMethod,
    selectedInstallment,
    paymentFinancialCompany,
    paymentDate,
    description,
    staffRows,
    excludableStaffRoles,
    attach2Items,
    confirmInfo,
    contractDate,
    totalPrice,
  } = state;

  const newAddress = `${clientData.address}`.trim();
  // V1 과 동일: 방법 name 을 그대로 lowercase — 'cms' 만 영향(한글 값은 대소문자가 없다).
  const paymentMethod = selectedPaymentMethod && selectedPaymentMethod.name
      ? selectedPaymentMethod.name.toLowerCase()
      : '';
  const fmtItem = (v) => (v ? formatCommaNumber(v) : '');
  const fmtRate = (v) => (v === null || v === undefined ? '' : String(v));

  // '기타'(방식 없음, selectedInstallment === null)는 paymentOption 자체를 생략한다 —
  // 서버 V6 매퍼가 등록 명세에 없는 칸('기타{방식}1')을 fail-closed 로 막는다.
  const paymentOption = selectedInstallment
      ? {
        option: selectedInstallment.name,
        value: selectedInstallment.months === null ? '' : String(selectedInstallment.months || ''),
      }
      : undefined;

  const paymentDetails = {
    paymentMethod,
    paymentFinancialCompany: paymentFinancialCompany || '',
    paymentDate: getDateFromEpochTime(paymentDate),
  };
  if (paymentOption) paymentDetails.paymentOption = paymentOption;

  return {
    templateId,
    contractType,
    contractTitle,
    contractCreator,
    clientInfos: [
      {
        companyName: clientData.companyName
            .replace('(출고정지)', '')
            .replace('(거래중지_악성미수)', '')
            .replace('(거래중지-영본확인)', '')
            .replace('(거래정지)', ''),
        bizNo: clientData.cNo,
        address: newAddress,
        representative: clientData.ceo.name,
        birthday: getDateFromEpochTime(clientData.ceo.birthday),
        licenseNo: clientData.licenseNo,
        mobile: clientData.contact.mobile,
        email: clientData.contact.email,
        signMethod: ModuSignMethod.SECURE_LINK,
      }
    ],
    itemAmounts: {
      implant: fmtItem(itemAmounts.implant),
      material: fmtItem(itemAmounts.material),
      equipment: fmtItem(itemAmounts.equipment),
      etc: fmtItem(itemAmounts.etc),
    },
    promotionCode,
    itemDiscountRates: {
      implant: fmtRate(itemDiscountRates.implant),
      material: fmtRate(itemDiscountRates.material),
      equipment: fmtRate(itemDiscountRates.equipment),
      etc: fmtRate(itemDiscountRates.etc),
    },
    paymentDetails,
    staffSigners: staffRows.map(r => ({
      role: r.role,
      name: r.name,
      mobile: r.mobile,
      deptName: r.deptName,
      excluded: excludableStaffRoles.includes(r.role) ? !!r.excluded : false,
    })),
    attach2Items: attach2Items.map(item => ({
      group: item.group,
      checked: !!item.checked,
      code: item.code || '',
    })),
    confirmInfo: {
      branch: confirmInfo.branch,
      contractDate: getDateFromEpochTime(contractDate),
      installDate: getDateFromEpochTime(confirmInfo.installDate),
      center: confirmInfo.center || '',
    },
    amountNum: parseInt(totalPrice),
    description: description || '',
    requestMessage: "덴티움 물품매매계약서 송부드립니다. ",
  };
}
